import { Component, OnInit, OnDestroy, ViewChild, Input, AfterViewInit, forwardRef, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Logger } from 'src/app/api/helper/app-error-logger';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MyApp } from 'src/app/api/helper/app';
import { AlertController, IonInput, IonSelect } from '@ionic/angular';
import { BarcodeAuthGuard } from '../../barcode.guard';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { SystemService } from 'src/app/api/system.service';
import { NfcEvent } from 'src/app/api/model/model';
import { I18N } from 'src/app/api/helper/i18n';

const log = new Logger('InputSelect');

@Component({
    selector: 'app-input-select',
    templateUrl: './input-select.component.html',
    styleUrls: ['./input-select.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => InputSelect),
        multi: true
    }],
    standalone: false
})
export class InputSelect implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit, OnChanges {

    @ViewChild('select', { read: IonSelect, static: true }) select: IonSelect;

    @Input() listItems: any[] = [];

    @Input() titel: string;
    @Input() lines = 'none';
    @Input() textExpr = 'text';
    @Input() valueExpr = '';

    @Input() placeholder = '';
    @Input() disabled = false;
    @Input() readOnly = false;
    @Input() enterkeyhint = 'done';
    @Input() pattern = '.*';
    @Input() required = false;
    @Input() clearInput = false;
    @Input() isInvalid = false;
    @Input() uppercase = false;
    @Input() scanNfcButton = false;
    @Input() scanNfcReadOnlyErlaubt = false;
    @Input() scanNfcExternesEventHandling = false;
    @Input() scanBarcodeButton = false;
    @Input() labelPosition: 'stacked' = undefined;

    @Output() valueChanged = new EventEmitter<any>();
    @Output() lostFocus = new EventEmitter<any>();
    @Output() nfcGescannt = new EventEmitter<NfcEvent>();

    subscriptions: Subscription[] = [];

    isVisible = false;
    isExtraButtonZeile = false;

    autocapitalize: string = null;

    isClearButtonVisible = false;
    isEmptySpaceVisible = false;

    /** Wird ein Button direkt neben dem Eingabefeld angezeigt? */
    inputWithButton = false;

    // labelPosition = undefined; // stacked
    inputSlot: 'end' = 'end';

    app = MyApp.current;

    private _value: string;

    nfcAlert: any;
    nfcRegistrationId: number;

    private _textValue: string = undefined;

    // Whatever name for this (myValue) you choose here, use it in the .html file.
    public get myValue(): string { return this._value }

    public get myTextValue(): string {
        if (this._textValue === undefined) {
            if (this.textExpr && this.textExpr != this.valueExpr && this.listItems?.length) {
                const item = this.listItems.find(i => i[this.valueExpr] == this._value);

                if (item) {
                    this._textValue = item[this.textExpr];
                } else {
                    this._textValue = this._value;
                }
            } else {
                this._textValue = this._value;
            }
        }

        return this._textValue;
    }

    public set myValue(v: string) {
        // log?.debug('set myValue: ' + v);
        this._textValue = undefined;

        if (this.uppercase && v && typeof (v) === 'string') {
            v = v.toUpperCase();
        }

        if (v !== this._value) {
            this._value = v;
            this.onChange(v);
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private alertController: AlertController,
        private systemService: SystemService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        // log?.debug('changed ' + this.titel, changes);
        this._textValue = undefined;

        this.updateType();
    }

    onChange = (_) => {
        if (this.isVisible) {
            this.validate();
        }
    };

    onTouched = () => { };

    writeValue(value: any): void {
        // log?.debug('writeValue ' + this.titel, value);

        this.myValue = value;

        // setTimeout(() => {
        //     this.validate();
        // });
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
    }

    ngOnInit() {
        // log?.debug('ngOnInit ' + this.titel);

        this.isVisible = true;
        this.updateType();
        this.validate();
    }

    async ngAfterViewInit() {
        // log?.debug('ngAfterViewInit ' + this.titel);

        setTimeout(() => {
            this.validate();
        });
    }

    ngOnDestroy() {
        this.systemService.unregisterNfcReceiver(this.nfcRegistrationId);
    }

    async ionViewWillEnter() {
    }

    ionViewDidEnter() {
    }

    ionViewWillLeave() {
    }

    onClearValue() {
        this.myValue = null;
        this.valueChanged.next(this._value);

        // this.select.setFocus();
    }

    open() {
        try {
            this.select.open();
        } catch (err) {
            log.warn(Utils.getErrorMessage(err), err);
        }
    }

    updateType() {
        this.isClearButtonVisible = false;
        this.isExtraButtonZeile = false;

        if (this.uppercase) {
            this.autocapitalize = 'characters';
        } else {
            this.autocapitalize = null;
        }

        // if (this.plusMinusButtons && !this.disabled && !this.readOnly) {
        //     this.isPlusButtonVisible = true;
        //     this.isMinusButtonVisible = true;
        // }

        if (this.clearInput && !this.scanNfcButton && !this.scanBarcodeButton) {
            this.isClearButtonVisible = true;
        }

        if (!this.scanBarcodeButton && !this.scanNfcButton && !this.isClearButtonVisible) {
            this.isEmptySpaceVisible = true;
        }

        if (this.scanBarcodeButton && this.scanNfcButton) {
            this.isExtraButtonZeile = true;
        }

        if (this.labelPosition === 'stacked') {
            this.inputSlot = undefined;
        } else {
            this.inputSlot = 'end';
        }

        this.inputWithButton = (this.isClearButtonVisible || this.scanNfcButton) && !this.isExtraButtonZeile;
    }

    onBlur(e: any) {
        // log?.debug('onBlur ' + this.titel);
        this.lostFocus.next(e);
        this.valueChanged.next(this._value);
        this.validate();
    }

    onValueChange(e: any) {
        // log?.debug('onValueChange ' + this.titel);
        this.valueChanged.next(this._value);
        this.validate();
    }

    validate() {
        // log?.debug('validate ' + this.titel + ': ' + this.pattern + ', value=' + this._value);

        if (this.pattern && this.pattern != '.*') {
            this.isInvalid = false;

            try {
                const wert = Utils.trimToEmpty(this._value);

                if (!wert.match(this.pattern)) {
                    this.isInvalid = true;
                }

            } catch (err) {
                log.warn('Fehler in Regex ' + this.pattern + ': ' + Utils.getErrorMessage(err));
            }
        }

        return !this.isInvalid;
    }

    async scanBarcode() {
        log?.debug('scanEigenschaftBarcode: ' + this.titel);

        BarcodeAuthGuard.barcodeScanAktiv = true;

        try {
            let barcodeData = await MyApp.scanBarcode();

            log?.debug(`scanEigenschaftBarcode ${this.titel}: ${barcodeData.text}`);

            if (barcodeData.text) {
                this.myValue = barcodeData.text;

                // this.cdr.detectChanges();
            }
        } catch (err) {
            UiHelper.showError(err);
        }

        setTimeout(() => BarcodeAuthGuard.barcodeScanAktiv = false, 500);

        this.cdr.detectChanges();
    }

    async scanNfc() {
        log?.debug('scanNfc');

        if (!this.scanNfcReadOnlyErlaubt) {
            if (this.readOnly || this.disabled) {
                return;
            }
        }

        this.nfcRegistrationId = this.systemService.registerNfcReceiver(100, (nfcEvent) => this.verarbeiteNfcEvent(nfcEvent));

        BarcodeAuthGuard.barcodeScanAktiv = true;

        if (this.nfcAlert) {
            this.nfcAlert.dismiss();
            this.nfcAlert = null;
        }

        this.nfcAlert = await this.alertController.create({
            mode: 'ios',
            message: I18N.instant('NFCErkennungLaeuft'),
            buttons: [{
                text: I18N.instant('Abbrechen'),
                role: 'cancel',
                cssClass: 'secondary',
                handler: () => {
                }
            },
            ]
        });

        if (!MyApp.isCordovaAvailable()) {
            setTimeout(() => {
                this.nfcAlert.dismiss();

                if (this.scanNfcExternesEventHandling) {
                    this.nfcGescannt.next({ seriennummer: 'DEMO12345' });
                    return;
                }

                this.myValue = 'DEMO12345';
            }, 2000);
        }

        await this.nfcAlert.present();

        await this.nfcAlert.onDidDismiss();

        this.systemService.unregisterNfcReceiver(this.nfcRegistrationId);

        // if (this.auftragdetails) {
        //     this.auftragdetails.starteAuftragWennErforderlich('scanEigenschaftNfc');
        // }

        setTimeout(() => BarcodeAuthGuard.barcodeScanAktiv = false, 500);

        this.cdr.detectChanges();
    }

    verarbeiteNfcEvent(event: NfcEvent) {
        if (this.scanNfcExternesEventHandling) {
            this.nfcGescannt.next(event);
        } else {
            let barcode: string;

            if (MyApp.current.NfcPayloadVerwenden && event.payload) {
                barcode = event.payload;
            } else {
                barcode = event.seriennummer;
            }

            this.myValue = barcode;
        }

        if (this.nfcAlert) {
            this.nfcAlert.dismiss();
            this.nfcAlert = null;
        }
    }
}
